/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FiUser } from 'react-icons/fi';
import AppTextInput from '../../../../shared/components/app-text-input/AppTextInput';
import AppSelect from '../../../../shared/components/app-select/AppSelect';
import AppDatePicker from '../../../../shared/components/app-date-picker/AppDatePicker';
import AppCheckBox from '../../../../shared/components/app-check-box/AppCheckBox';
import {
  COUNTRY_OPTIONS,
  countryMobileCodes,
} from '../../../../shared/constants/CountryOptions';
import {
  GENDER_OPTIONS,
  TITLE_GENDER,
  TITLE_OPTIONS,
} from '../../constants/cruiseConstants';
import { dobRange } from '../../utils/cruiseGuestValidation';
import { parseApiDate, toApiDate } from '../../utils/cruiseUtils';

const TYPE_LABEL = { adult: 'Adult', child: 'Child', infant: 'Infant' };

// The app's shared country lists (same ones flight / visa / eSIM use).
const MOBILE_CODE_OPTIONS = countryMobileCodes.map((c) => ({
  value: c.code,
  label: `${c.name} (${c.dial_code})`,
  dial: c.dial_code,
}));

const ISSUE_PLACE_OPTIONS = COUNTRY_OPTIONS.map((c) => ({
  value: c.label,
  label: c.label,
}));

/**
 * The shared inputs only show their error text while the box is empty, so
 * an error on a filled-in box (wrong format, out-of-range date) is shown
 * here instead.
 */
function FieldError({ value, error }) {
  if (!error || !value || String(value).trim() === '') return null;
  return <small className="app-error-text cr-format-error">{error}</small>;
}

FieldError.propTypes = {
  value: PropTypes.string,
  error: PropTypes.string,
};

FieldError.defaultProps = {
  value: '',
  error: '',
};

function CruiseGuestForm({
  index,
  guest,
  lead,
  showAddress,
  sameAddress,
  onToggleSameAddress,
  errors,
  onChange,
  onTouch,
  sailDate,
  endDate,
}) {
  const set = (field) => (e) => onChange(field, e.target.value);
  const setUpper = (field) => (e) =>
    onChange(field, e.target.value.toUpperCase());
  const setDigits = (field) => (e) =>
    onChange(field, e.target.value.replace(/[^0-9]/g, ''));

  const text = (field, label, opts = {}) => (
    <div className={`cr-form-cell ${opts.wide ? 'cr-form-cell--wide' : ''}`}>
      <AppTextInput
        name={`${field}${index}`}
        label={label}
        value={guest[field] || ''}
        onChange={opts.onChange || set(field)}
        onBlur={() => onTouch(field)}
        placeholderText={opts.placeholder || ''}
        errorText={errors[field] || ''}
        required={opts.required}
        type={opts.type || 'text'}
        maxLength={opts.maxLength}
        inputMode={opts.inputMode}
      />
      <FieldError value={guest[field]} error={errors[field]} />
    </div>
  );

  const select = (field, label, options, opts = {}) => (
    <div className={`cr-form-cell ${opts.wide ? 'cr-form-cell--wide' : ''}`}>
      <AppSelect
        name={`${field}${index}`}
        label={label}
        placeholder={opts.placeholder || ''}
        isClearable={false}
        required={opts.required}
        options={options}
        selectedValue={guest[field]}
        errorText={errors[field] || ''}
        onBlur={() => onTouch(field)}
        onSelect={(opt) => {
          if (opts.onSelect) opts.onSelect(opt);
          else onChange(field, opt.value);
          onTouch(field);
        }}
      />
    </div>
  );

  const date = (field, label, { minDate, maxDate, openToDate }) => (
    <div className="cr-form-cell">
      <AppDatePicker
        name={`${field}${index}`}
        label={label}
        selectedDate={parseApiDate(guest[field])?.toDate() || ''}
        onChange={(d) => {
          onChange(field, toApiDate(d));
          onTouch(field);
        }}
        onCalendarClose={() => onTouch(field)}
        errorText={errors[field] || ''}
        required
        showYearDropdown
        showMonthDropdown
        dropdownMode="select"
        customClassName="cr-date-input"
        customMobileClass="cr-date-mobile"
        labelClassName="cr-date-label"
        valueClassName="cr-date-value"
        minDate={minDate || ''}
        maxDate={maxDate || ''}
        openToDate={openToDate}
      />
      <FieldError value={guest[field]} error={errors[field]} />
    </div>
  );

  const titleOptions = TITLE_OPTIONS[guest.paxType] || TITLE_OPTIONS.adult;

  return (
    <div className="cr-panel cr-panel--pad cr-guest">
      <div className="cr-guest__head">
        <span className="cr-guest__icon">
          <FiUser size={16} />
        </span>
        <b>Guest {index + 1}</b>
        <span className="cr-muted-sm">{TYPE_LABEL[guest.paxType]}</span>
        {lead && <span className="cr-badge cr-badge--accent">Lead guest</span>}
      </div>

      <div className="cr-form-grid">
        {select('salutation', 'Title', titleOptions, {
          required: true,
          onSelect: (opt) => {
            onChange('salutation', opt.value);
            if (TITLE_GENDER[opt.value]) {
              onChange('gender', TITLE_GENDER[opt.value]);
            }
          },
        })}
        {text('firstName', 'First name', { required: true })}
        {text('middleName', 'Middle name', { placeholder: 'Optional' })}
        {text('lastName', 'Last name', { required: true })}
        {select('gender', 'Gender', GENDER_OPTIONS, { required: true })}
        {date(
          'dateOfBirth',
          'Date of birth',
          dobRange(guest.paxType, sailDate),
        )}
        {text('nationality', 'Nationality', { required: true })}
        {text('panNumber', 'PAN number', {
          onChange: setUpper('panNumber'),
          maxLength: 10,
        })}
      </div>

      <div className="cr-form-section">Passport</div>
      <div className="cr-form-grid">
        {text('passportNumber', 'Passport number', {
          required: true,
          onChange: setUpper('passportNumber'),
          maxLength: 12,
        })}
        {date('passportExpiry', 'Expiry date', {
          minDate:
            parseApiDate(endDate || sailDate)?.toDate() || moment().toDate(),
          maxDate: moment().add(20, 'years').toDate(),
        })}
        {select('passportIssuePlace', 'Place of issue', ISSUE_PLACE_OPTIONS, {
          placeholder: 'Select country',
        })}
      </div>

      <div className="cr-form-section">Contact</div>
      <div className="cr-form-grid">
        {text('email', 'Email', {
          required: lead,
          type: 'email',
          wide: true,
        })}
        {select('countryIso', 'Mobile country code', MOBILE_CODE_OPTIONS, {
          required: lead,
          placeholder: 'Select country',
          onSelect: (opt) => {
            onChange('countryIso', opt.value);
            onChange('countryCode', opt.dial);
          },
        })}
        {text('phoneNo', 'Mobile number', {
          required: lead,
          onChange: setDigits('phoneNo'),
          maxLength: 15,
          inputMode: 'numeric',
        })}
      </div>

      {!lead && (
        <AppCheckBox
          id={`cr-same-address-${index}`}
          name="sameAddress"
          checked={sameAddress}
          onChange={onToggleSameAddress}
          labelClassName="cr-check-label"
          labelText="Same address as lead guest"
        />
      )}

      {showAddress && (
        <>
          <div className="cr-form-section">Address</div>
          <div className="cr-form-grid">
            {text('address', 'Street address', {
              required: true,
              wide: true,
            })}
            {text('city', 'City', { required: true })}
            {text('state', 'State', { required: true })}
            {text('country', 'Country', { required: true })}
            {text('pinCode', 'PIN code', { required: true, maxLength: 10 })}
          </div>
        </>
      )}
    </div>
  );
}

CruiseGuestForm.propTypes = {
  index: PropTypes.number.isRequired,
  guest: PropTypes.object.isRequired,
  lead: PropTypes.bool.isRequired,
  showAddress: PropTypes.bool.isRequired,
  sameAddress: PropTypes.bool,
  onToggleSameAddress: PropTypes.func,
  errors: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onTouch: PropTypes.func,
  sailDate: PropTypes.string,
  endDate: PropTypes.string,
};

CruiseGuestForm.defaultProps = {
  sameAddress: true,
  onToggleSameAddress: () => {},
  errors: {},
  onTouch: () => {},
  sailDate: '',
  endDate: '',
};

export default CruiseGuestForm;
